import { useDisclosure } from '@mantine/hooks'
import { Close, Menu } from '@mui/icons-material'
import { AppBar, Box, IconButton, Toolbar, useTheme } from '@mui/material'
import { ForwardedRef, forwardRef } from 'react'
import { useLocation } from 'react-router-dom'

import useUserAccess from '~/features/auth/hooks/useUserAccess'
import EElement from '~/features/ui/models'
import ImpersonationBanner from '~/layouts/account/header/impersonationBanner'
import Logo from '~/layouts/account/header/logo'
import SideNav from '~/layouts/account/header/nav/side'
import PortalAccount from '~/layouts/app/header/account/portal'
import { EClientPath } from '~/models/api'
import rem from '~/utils/ui'

const Header = forwardRef((_, ref: ForwardedRef<HTMLElement>) => {
  const [isOpen, { open, close }] = useDisclosure(false)
  const theme = useTheme()

  const location = useLocation()
  const { isPlusUser } = useUserAccess()
  const isSideNavCollapsed =
    location.pathname.startsWith(EClientPath.inbox) ||
    (isPlusUser && location.pathname.startsWith(EClientPath.scenariosBase))

  return (
    <>
      <AppBar
        component="nav"
        ref={ref}
        id={EElement.accountHeader}
        position="fixed"
        sx={{
          backgroundColor: theme.palette.menuBackground,
          zIndex: theme.zIndex.drawer + 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'stretch',
          flexDirection: 'column',
          '&.MuiPaper-root': {
            boxShadow: 'none',
          },
        }}
      >
        <ImpersonationBanner />
        <Toolbar
          disableGutters
          sx={{
            ...theme.mixins.accountToolbar,
            width: '100%',
            paddingInline: rem(12),
            paddingBlock: { xxxs: rem(24), md: rem(12) },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <IconButton
              aria-label="open drawer"
              onClick={isOpen ? close : open}
              sx={{
                color: theme.palette.menuIcon,
                width: rem(48),
                height: rem(48),
                display: { md: 'none' },
              }}
            >
              {isOpen ? <Close /> : <Menu />}
            </IconButton>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: { xxxs: 'center', md: 'left' },
              }}
            >
              <Logo />
            </Box>
            <PortalAccount />
          </Box>
        </Toolbar>
      </AppBar>
      <SideNav isOpen={isOpen} close={close} isCollapsed={isSideNavCollapsed} />
    </>
  )
})

export default Header
